import { useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Drawer } from '@mantine/core';
import { faSpinner } from '@fortawesome/pro-solid-svg-icons/faSpinner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { FreightDetails } from '@shipstation/client-common/components/freight-details';
import { mapBackendShipmentToLtlShipment } from '@shipstation/client-common/ltl/freight-payload-mappers';
import { FlexRow } from 'ui-components/components/flex/row';
import { Gap } from 'ui-components/components/flex/gap';
import { useMaxZ } from 'ui-components/hooks/services/use-max-z';

import Global from '../../../../../../store/global-selectors';
import { goToOrderDetails } from '../../../../../../store/orders/order-details/orders.details.actions';
import { useModalManagerPortal } from '../../../../../../portals/hooks';
import { downloadDocumentRequest, trackShipmentRequest } from '../../../../../../store/ltl/ltl.actions';
import { getFreightGridRequest } from '../../../../../../store/ltl/grid/ltl.grid.actions';
import { useModal } from '../../../../../../hooks/use-modal';
import { useVoidFreight } from '../../../../../../hooks/freight/use-void-freight';
import { ConfirmDialogueModal } from '../../../../../../components/modals/confirm-dialogue-modal';
import { freightActionButtonMessages } from '../../../freight-action-buttons/messages';
import { bookingConfirmationViewMessages } from './booking-confirmation-view-messages';

import type { ShipFrom } from '@shipstation/client-common/types/ship-from';
import type { BaseWeightUnit, WeightUnit } from '@shipstation/client-common/types/weight';
import type { DimensionUnit } from '@shipstation/client-common/types/dimensions';
import type { FreightShipment } from '@shipstation/client-common/types/freight-responses';
import type { LtlAddress, LtlBookingConfirmation, LtlDocument } from '@shipstation/client-common/types/ltl';

import style from './booking-confirmation-view.module.less';

const DEFAULT_COUNTRY_CODE = 'US';

// Seller weight preference is stored as a base unit ('Ounces' or 'Grams'); the
// freight-details view displays totals in the corresponding major unit. Mirrors the
// wizard container's DISPLAY_WEIGHT_UNIT_BY_BASE.
const DISPLAY_WEIGHT_UNIT_BY_BASE: Record<BaseWeightUnit, WeightUnit> = {
  Ounces: 'Pounds',
  Grams: 'Kilograms',
};

/**
 * Resolves the origin `LtlAddress` for a booked shipment. The typed `FreightShipment`
 * exposes no origin address, so we look it up from the seller ship-froms by matching
 * `shipFromWarehouseId` and build an `LtlAddress` from the matched record's loose
 * `originAddress` shape. Returns null when there is no match or no address on file.
 */
const resolveOriginLtlAddress = (shipment: FreightShipment | null, shipFroms: ShipFrom[]): LtlAddress | null => {
  if (!shipment) return null;
  const shipFrom = shipFroms.find((sf) => String(sf.shipFromId) === String(shipment.shipFromWarehouseId ?? ''));
  const sfAddr = shipFrom?.originAddress;
  if (!shipFrom || !sfAddr) return null;
  return {
    name: sfAddr.name ?? '',
    company: sfAddr.company ?? shipFrom.name ?? '',
    line1: sfAddr.line1 ?? '',
    line2: sfAddr.line2 ?? '',
    city: sfAddr.city ?? '',
    state: sfAddr.state ?? '',
    postalCode: sfAddr.postalCode ?? '',
    countryCode: sfAddr.countryCode ?? DEFAULT_COUNTRY_CODE,
    phone: sfAddr.phone ?? '',
    email: sfAddr.email ?? '',
    locationType: '',
  };
};

type BookingConfirmationViewProps = {
  freightShipmentId: string;
  bookingConfirmation: LtlBookingConfirmation | null;
  onClose: () => void;
};

export const BookingConfirmationView = ({ freightShipmentId, bookingConfirmation, onClose }: BookingConfirmationViewProps) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  // The entities selector returns a runtime FreightShipment; the typed selector is
  // loosely typed, so cast to the canonical backend shape we feed the mapper.
  const rawShipment = useSelector((state) =>
    freightShipmentId ? Global.selectEntitiesFreightShipmentById(state, { freightShipmentId }) : null,
  ) as FreightShipment | null;

  const { orderNumber, salesOrderId } = useSelector((state) => {
    const fpId = rawShipment?.fulfillmentPlanId;
    if (!fpId) return { orderNumber: undefined, salesOrderId: undefined };
    const fp = Global.selectEntitiesFulfillmentPlanById(state, { fulfillmentPlanId: fpId });
    const fpSalesOrderId = fp?.salesOrdersFulfilledIds?.[0];
    const fpOrderNumber = fpSalesOrderId
      ? (Global.selectEntitiesSalesOrderProperty(state, { salesOrderId: fpSalesOrderId, property: 'orderNumber' }) ?? undefined)
      : undefined;
    return { orderNumber: fpOrderNumber, salesOrderId: fpSalesOrderId };
  }, shallowEqual);

  const shipFroms: ShipFrom[] = useSelector(Global.selectSellerShipFroms);
  const weightUnit = useSelector(Global.selectSellerDefaultWeightUnit) as BaseWeightUnit;
  const dimensionUnit = useSelector(Global.selectSellerDefaultDimensionUnit) as DimensionUnit;
  const displayWeightUnit = DISPLAY_WEIGHT_UNIT_BY_BASE[weightUnit] ?? 'Pounds';
  const displayDimensionUnit = dimensionUnit;

  // Portal into ss-ui-client's ModalManager (inside `.app`) rather than Mantine's
  // default body target, so this Drawer shares a stacking context with the native
  // print modal it can launch (Print BOL). `useMaxZ` then orders them correctly.
  const modalManagerRef = useModalManagerPortal();
  const zIndex = useMaxZ();
  const { isOpen: isVoidModalOpen, open: openVoidModal, close: closeVoidModal } = useModal();
  const voidFreight = useVoidFreight();

  const resolvedOrigin = useMemo<LtlAddress | null>(() => {
    return resolveOriginLtlAddress(rawShipment, shipFroms);
  }, [rawShipment, shipFroms]);

  const ltlShipment = useMemo(
    () => (rawShipment ? mapBackendShipmentToLtlShipment(rawShipment, resolvedOrigin) : null),
    [rawShipment, resolvedOrigin],
  );

  const onPrintDocument = useCallback(
    (document: LtlDocument) => {
      if (!ltlShipment) return;
      dispatch(downloadDocumentRequest(ltlShipment.freightShipmentId, document.key, document.documentType));
    },
    [dispatch, ltlShipment],
  );

  const onTrack = useCallback(() => {
    if (!ltlShipment) return;
    dispatch(trackShipmentRequest(ltlShipment.freightShipmentId));
  }, [dispatch, ltlShipment]);

  const handleOrderLinkClick = useCallback(() => {
    if (!salesOrderId || !rawShipment?.fulfillmentPlanId) return;
    dispatch(
      goToOrderDetails({ salesOrderId, fulfillmentPlanId: rawShipment.fulfillmentPlanId, selectOrderInGrid: false, replaceRoute: false }),
    );
  }, [dispatch, rawShipment, salesOrderId]);

  const handleVoidConfirm = useCallback(async () => {
    closeVoidModal();
    const fulfillmentPlanId = rawShipment?.fulfillmentPlanId;
    if (!fulfillmentPlanId) return;
    const didSucceed = await voidFreight([fulfillmentPlanId]);
    if (didSucceed) {
      dispatch(getFreightGridRequest());
      onClose();
    }
  }, [closeVoidModal, dispatch, onClose, rawShipment, voidFreight]);

  const portalProps = useMemo(() => ({ target: modalManagerRef ?? undefined }), [modalManagerRef]);

  if (!ltlShipment) {
    return (
      <Drawer
        classNames={{ content: style['drawer-content'] }}
        data-ignore-root-focus
        onClose={onClose}
        opened
        padding={0}
        portalProps={portalProps}
        position='right'
        size='100%'
        title={intl.formatMessage(bookingConfirmationViewMessages.title)}
        zIndex={zIndex}
      >
        <FlexRow className={style['spinner-state']} gap={Gap.Space8}>
          <FontAwesomeIcon icon={faSpinner} spin />
        </FlexRow>
      </Drawer>
    );
  }

  return (
    <>
      <FreightDetails
        bookingConfirmation={bookingConfirmation}
        displayDimensionUnit={displayDimensionUnit}
        displayWeightUnit={displayWeightUnit}
        isOpen
        onClose={onClose}
        onOrderLinkClick={orderNumber ? handleOrderLinkClick : undefined}
        onPrintDocument={onPrintDocument}
        onTrack={onTrack}
        onVoid={rawShipment?.fulfillmentPlanId ? openVoidModal : undefined}
        orderNumber={orderNumber}
        originAddress={resolvedOrigin}
        portalRef={modalManagerRef ?? null}
        shipment={ltlShipment}
        zIndex={zIndex}
      />
      {isVoidModalOpen && (
        <ConfirmDialogueModal
          body={<FormattedMessage {...freightActionButtonMessages.voidConfirmBody} />}
          buttonMessage={<FormattedMessage {...freightActionButtonMessages.voidConfirmButton} />}
          buttonTheme='danger'
          header={<FormattedMessage {...freightActionButtonMessages.voidConfirmHeader} />}
          onClose={closeVoidModal}
          onConfirm={handleVoidConfirm}
        />
      )}
    </>
  );
};
